import type { CreditSpreadChainRow, OptionRight } from '@fleece/models';
import { CHAIN_COLUMNS, STRIKE_HELP } from '@/lib/chain-columns';
import * as fmt from '@/lib/format';
import type { PickedSpread } from '@/lib/spread';
import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface ChainTableProps {
  readonly right: OptionRight;
  readonly rows: ReadonlyArray<CreditSpreadChainRow>;
  readonly picked: ReadonlyArray<CreditSpreadChainRow>;
  readonly spread?: PickedSpread;
  readonly onPick: (row: CreditSpreadChainRow) => void;
}

/**
 * The out-of-the-money contracts of one expiration, nearest the money first. A row is a
 * button: tapping two picks a spread, and the one nearer the money is the one sold.
 *
 * On a phone the table scrolls sideways under a sticky strike column, so the row being
 * read never loses its label.
 */
export function ChainTable({ right, rows, picked, spread, onPick }: ChainTableProps) {
  const role = (row: CreditSpreadChainRow): 'short' | 'long' | 'picked' | undefined => {
    if (spread?.short.symbol === row.symbol) {
      return 'short';
    }
    if (spread?.long.symbol === row.symbol) {
      return 'long';
    }
    return picked.some((p) => p.symbol === row.symbol) ? 'picked' : undefined;
  };

  return (
    <div className="scrollbar-thin max-h-[60vh] overflow-auto">
      <table className="w-full border-separate border-spacing-0 text-xs tabular">
        <ChainHeader right={right} />
        <tbody>
          {rows.map((row) => {
            const r = role(row);
            return (
              <tr
                key={row.symbol}
                onClick={() => onPick(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onPick(row);
                  }
                }}
                tabIndex={0}
                aria-selected={r !== undefined}
                className={cn('group cursor-pointer outline-none', r !== undefined ? 'bg-accent-soft' : 'hover:bg-surface-2 focus-visible:bg-surface-2')}
              >
                <td
                  className={cn(
                    'sticky left-0 z-[1] border-b border-line py-1 pr-2 pl-2.5 font-semibold whitespace-nowrap',
                    r !== undefined ? 'bg-accent-soft' : 'bg-surface group-hover:bg-surface-2',
                  )}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <RoleBadge role={r} />
                    {fmt.strike(row.strike)}
                  </span>
                </td>
                {CHAIN_COLUMNS.map((column) => (
                  <td key={column.key} className="border-b border-line px-2 py-1 text-right whitespace-nowrap text-ink-2 group-hover:text-ink">
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RoleBadge({ role }: { role: 'short' | 'long' | 'picked' | undefined }) {
  if (role === undefined) {
    return <span aria-hidden className="inline-block w-3.5" />;
  }
  const label = role === 'short' ? 'S' : role === 'long' ? 'L' : '•';
  return (
    <span
      title={role === 'short' ? 'Sold' : role === 'long' ? 'Bought' : 'Picked: pick one more in this expiration'}
      className="inline-flex size-3.5 items-center justify-center rounded-sm bg-accent text-[9px] leading-none font-bold text-accent-ink"
    >
      {label}
    </span>
  );
}

/** Shared by the table and its skeleton, so the placeholder's columns are the table's columns. */
function ChainHeader({ right }: { right: OptionRight }) {
  return (
    <thead className="sticky top-0 z-[2] bg-surface">
      <tr className="text-[11px] text-ink-2">
        <th title={STRIKE_HELP.description} className="sticky left-0 z-[3] border-b border-line bg-surface py-1 pr-2 pl-2.5 text-left font-medium">
          {right === 'put' ? 'Put' : 'Call'} strike
        </th>
        {CHAIN_COLUMNS.map((column) => (
          <th key={column.key} title={column.description} className="border-b border-line px-2 py-1 text-right font-medium whitespace-nowrap">
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * The table's footprint while the chain loads: the real header over rows of the real
 * height, so the columns are already where the data will put them.
 */
export function ChainTableSkeleton({ right, rows = 12 }: { right: OptionRight; rows?: number }) {
  return (
    <div role="status" aria-label="Loading the chain" className="scrollbar-thin max-h-[60vh] overflow-hidden">
      <table className="w-full border-separate border-spacing-0 text-xs tabular">
        <ChainHeader right={right} />
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              <td className="border-b border-line py-1 pr-2 pl-2.5">
                {/* h-4 is a text-xs line: the placeholder row is exactly as tall as a real one. */}
                <span className="flex h-4 items-center gap-1.5">
                  <span className="inline-block w-3.5" />
                  <Skeleton className="h-3 w-8" />
                </span>
              </td>
              {CHAIN_COLUMNS.map((column) => (
                <td key={column.key} className="border-b border-line px-2 py-1">
                  <span className="flex h-4 items-center justify-end">
                    <Skeleton className="h-3 w-8" />
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
